import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle2, XCircle, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/constants";

export default function DispatchView() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [choice, setChoice] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/dispatch/${token}`).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [token]);

  const respond = async () => {
    if (!choice) return toast.error("Escolha 'Aceito' ou 'Não Aceito' antes de confirmar.");
    setSending(true);
    try {
      await api.post(`/dispatch/${token}/respond`, { response: choice, message });
      toast.success("Resposta registrada!");
      // refresh dispatch
      const r = await api.get(`/dispatch/${token}`);
      setD(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (err) return (
    <div className="container-page py-24 max-w-2xl" data-testid="dispatch-error">
      <div className="bg-white border border-cream-400 p-10 text-center">
        <XCircle className="w-10 h-10 text-terracotta-500 mx-auto mb-4" />
        <h1 className="font-display text-3xl text-forest-900">Encaminhamento não encontrado</h1>
        <p className="text-forest-900/70 mt-3">{err}</p>
      </div>
    </div>
  );

  if (!d) return <div className="container-page py-24 text-forest-900/60">Carregando...</div>;

  const alreadyResponded = d.response === "aceito" || d.response === "nao_aceito";

  return (
    <div data-testid="dispatch-view-page" className="py-16">
      <div className="container-page max-w-3xl">
        <div className="bg-forest-900 text-cream-100 px-6 py-5 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-copaiba-300" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-cream-100/70">Secretaria Virtual</div>
            <div className="font-display text-xl leading-tight">COOMFLONA — Encaminhamento oficial</div>
          </div>
        </div>

        <article className="bg-white border border-t-0 border-cream-400 p-8 md:p-10">
          <div className="eyebrow mb-3">Para: {d.cooperado_name}</div>
          <h1 className="font-display text-3xl md:text-4xl text-forest-900 leading-tight">{d.title}</h1>
          <div className="text-xs text-forest-900/60 mt-2">
            Enviado por {d.secretary_name} · {formatDate(d.created_at)}
          </div>
          <div className="section-divider my-6" />
          <div className="text-forest-900/90 leading-relaxed whitespace-pre-wrap">{d.body}</div>

          <div className="mt-10">
            {alreadyResponded ? (
              <div className={`p-6 border ${d.response === "aceito" ? "bg-forest-700/5 border-forest-700" : "bg-terracotta-500/5 border-terracotta-500"}`} data-testid="already-responded">
                <div className="flex items-center gap-3 mb-2">
                  {d.response === "aceito"
                    ? <><CheckCircle2 className="w-6 h-6 text-forest-700" /><div className="font-display text-2xl text-forest-700">Resposta registrada: ACEITO</div></>
                    : <><XCircle className="w-6 h-6 text-terracotta-500" /><div className="font-display text-2xl text-terracotta-500">Resposta registrada: NÃO ACEITO</div></>
                  }
                </div>
                <p className="text-sm text-forest-900/70">Registrado em {formatDate(d.response_at)}.</p>
                {d.response_message && (
                  <div className="mt-4 bg-white border border-cream-400 p-4 text-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-forest-700 mb-1">Sua resposta</div>
                    {d.response_message}
                  </div>
                )}
                <p className="text-sm text-forest-900/70 mt-4 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-copaiba-700" />
                  Obrigado! Sua resposta foi enviada à secretaria.
                </p>
              </div>
            ) : (
              <div>
                <h2 className="font-display text-2xl text-forest-900">Sua resposta</h2>
                <p className="text-sm text-forest-900/70 mt-1">Confirme se você aceita ou não os termos deste encaminhamento.</p>

                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <button
                    type="button"
                    onClick={() => setChoice("aceito")}
                    data-testid="choose-accept"
                    className={`p-5 border text-left transition-all ${choice === "aceito" ? "bg-forest-700 text-cream-100 border-forest-700" : "bg-white text-forest-900 border-cream-400 hover:border-forest-700"}`}
                  >
                    <CheckCircle2 className={`w-6 h-6 mb-2 ${choice === "aceito" ? "text-copaiba-300" : "text-forest-700"}`} />
                    <div className="font-display text-xl">Aceito</div>
                    <div className={`text-xs mt-1 ${choice === "aceito" ? "text-cream-100/80" : "text-forest-900/65"}`}>Confirmo que recebi e estou de acordo.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setChoice("nao_aceito")}
                    data-testid="choose-refuse"
                    className={`p-5 border text-left transition-all ${choice === "nao_aceito" ? "bg-terracotta-500 text-cream-100 border-terracotta-500" : "bg-white text-forest-900 border-cream-400 hover:border-terracotta-500"}`}
                  >
                    <XCircle className={`w-6 h-6 mb-2 ${choice === "nao_aceito" ? "text-cream-100" : "text-terracotta-500"}`} />
                    <div className="font-display text-xl">Não Aceito</div>
                    <div className={`text-xs mt-1 ${choice === "nao_aceito" ? "text-cream-100/85" : "text-forest-900/65"}`}>Não estou de acordo com este encaminhamento.</div>
                  </button>
                </div>

                <div className="mt-6">
                  <Label>Observação / justificativa (opcional)</Label>
                  <Textarea
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="response-message"
                    placeholder="Se quiser, deixe uma observação à secretaria..."
                  />
                </div>

                <Button
                  type="button"
                  disabled={!choice || sending}
                  onClick={respond}
                  data-testid="submit-response"
                  className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none mt-6 px-8 py-6 text-base"
                >
                  {sending ? "Registrando..." : "Confirmar e enviar resposta"}
                </Button>
              </div>
            )}
          </div>
        </article>

        <p className="text-xs text-forest-900/55 text-center mt-6">
          Documento gerado pela Secretaria Virtual da COOMFLONA — coomflona.com.br
        </p>
      </div>
    </div>
  );
}
